/**
 * Reading a card's data again WITHOUT ITS SKELETON — the rule of the pods
 * card (`podsFollowUp.ts`) for the other cards of the branch page
 * (`ResourcesCard`, `WarningEventsCard`, `BranchCodeLine`):
 *
 *  - when the page says so: a new `refreshKey` (its Refresh, a change of the
 *    Application it learnt of) — a prop rather than a new React key, so the
 *    card stays mounted with its last answer on screen meanwhile;
 *  - on a return to the tab, when the last answer is older than
 *    {@link QUIET_REFRESH_STALE_MS}.
 *
 * One read at a time (`createSingleFlight`). What a read writes, and that a
 * failed one leaves the last answer on screen, is the card's business.
 */

import { useEffect, useRef, useState } from 'react';
import { createSingleFlight } from './podsFollowUp';
import { useOnDocumentVisible } from './visiblePolling';

/** How old an answer may be on a return to the tab before being read again. */
export const QUIET_REFRESH_STALE_MS = 30_000;

/**
 * Calls `onChange` each time `value` changes after the first render — not
 * on mount, and not for the value it was mounted with.
 */
export function useOnValueChange(value: unknown, onChange: () => void): void {
  const latest = useRef(onChange);
  latest.current = onChange;
  const seen = useRef(value);
  useEffect(() => {
    if (Object.is(seen.current, value)) {
      return;
    }
    seen.current = value;
    latest.current();
  }, [value]);
}

export interface QuietRefreshOptions {
  /** A new value: one quiet read. */
  refreshKey: unknown;
  /**
   * When the card's last read settled (`Date.now()`), answered or not: a
   * failed one is tried again on the next return to the tab too.
   */
  lastReadAt: () => number | undefined;
  /** The quiet read; its failures are its own. */
  read: () => Promise<unknown>;
}

export function useQuietRefresh(options: QuietRefreshOptions): void {
  const latest = useRef(options);
  latest.current = options;
  const [run] = useState(() => createSingleFlight(() => latest.current.read()));

  // `true`: a change asked while a read is in flight may postdate it.
  useOnValueChange(options.refreshKey, () => {
    void run(true);
  });

  // Not before a first read has settled: the card's own is in flight.
  useOnDocumentVisible(() => {
    const readAt = latest.current.lastReadAt();
    if (readAt !== undefined && Date.now() - readAt > QUIET_REFRESH_STALE_MS) {
      void run(false);
    }
  });
}
